import baseApi, { fileUploadBaseQuery } from './base';
import { ElearningTag, FramesTag, MyElearningTag } from './tags';
import { FrameDataType } from '@/modules/types/frame';

export interface PptImportResponse {
  elearningId: string;
  slideCount: number;
}

/** Response of GET elearnings/:id/import-status — the lightweight conversion poll. */
export interface ImportStatusResponse {
  /** null when the elearning has no import metadata (not a PPT import). */
  importStatus: 'queued' | 'importing' | 'ready' | 'failed' | null;
  /** User-facing failure reason (set when importStatus is 'failed'); optional for
   *  back-compat with API versions that predate it. */
  importError?: string | null;
  framesDone: number;
  totalSlides: number;
}

/**
 * Read-only pre-check: can this tenant start a new PPT import right now?
 * Advisory — the import endpoint re-checks atomically; this only lets the UI
 * block the upload up-front instead of after the file has been uploaded.
 */
export interface ImportCapacity {
  canStart: boolean;
  reason?: 'concurrency' | 'cap';
  activeImports: number;
  maxConcurrentImports: number;
  used: number;
  cap: number;
}

// Help article for the "Generate Course from PowerPoint" feature, linked from the
// PPT-import alerts (converted-by-AI notice and the guardrail-blocked warning).
export const PPT_IMPORT_HELP_URL =
  'https://help.elmotalent.com.au/hc/en-us/articles/58812099522969-Generate-Course-from-PowerPoint?brand_id=69010';

export const getMonthlyCapMessage = (cap?: number): string =>
  cap == null
    ? 'Monthly AI request limit reached. Reach out to your Account Manager for assistance.'
    : `Monthly AI request limit (${cap}) reached. Reach out to your Account Manager for assistance.`;

// Fallback for a 503 on the sync tweak/regenerate path (SlideForm) — a Bedrock rate
// limit or another transient outage — used only when the body carries no message.
// Path-neutral (no "import") because that surface is a slide edit, not an import; the
// async import surfaces the API's own import-specific copy via importError.
export const HIGH_DEMAND_MESSAGE =
  "We're experiencing high demand right now. Please try again in a few minutes.";

// Single-action concurrency limit (AI_CONCURRENCY_LIMIT 403) on the slide-edit path —
// the limit is per tenant, so the wait is on someone else's edit, not this one.
export const AI_EDIT_BUSY_MESSAGE =
  'Your organisation has several AI slide edits in progress. Please wait for one to finish and try again.';

// The slide edit timed out at the gateway (504). The API keeps going and may still
// insert the slide, which only shows after a reload, so send the admin there instead
// of inviting a duplicate. "Save any changes" first: a browser reload has no
// unsaved-changes prompt (NavigatePrompt only guards in-app navigation).
export const SLIDE_EDIT_TIMEOUT_MESSAGE =
  'This is taking longer than usual. Your new slide may still be created. Save any changes, then reload the page in a moment to check before trying again.';

// Same timeout when a placeholder is being replaced in place: the form keeps showing
// the old content until the page is reloaded.
export const SLIDE_REPLACE_TIMEOUT_MESSAGE =
  'This is taking longer than usual. Save any changes, then reload the page in a moment to check whether the slide was replaced before trying again.';

export const REGENERATE_MODES = {
  TWEAK: 'tweak',
  REGENERATE: 'regenerate',
  KEEP_AS_IMAGE: 'keep-as-image',
} as const;

export interface RegenerateSlideRequest {
  elearningId: string;
  frameId: string;
  mode: 'regenerate' | 'keep-as-image';
  userInstruction?: string;
}

export interface TweakSlideRequest {
  elearningId: string;
  frameId: string;
  userInstruction: string;
}

const pptImportApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    importPpt: build.mutation<PptImportResponse, { name: string; file: File }>({
      queryFn: async ({ name, file }, api, extraOptions) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', name);

        const result = await fileUploadBaseQuery(
          {
            url: 'elearnings/import-ppt',
            method: 'POST',
            body: formData,
          },
          api,
          extraOptions,
        );

        return result as any;
      },
      invalidatesTags: [ElearningTag, MyElearningTag],
    }),

    /**
     * Polls the lightweight import-status endpoint during PPTX conversion (LMS2-5049).
     *
     * Single indexed query on the backend — no joins, no frame content — so it is
     * cheap enough for both the converting modal and the My Library list to poll.
     * Untagged on purpose: it must stay isolated from the main elearning cache so
     * mutations that invalidate ElearningTag don't reset the polling interval.
     *
     * Callers pass { pollingInterval } at the call site (5s+ — backend progress
     * granularity is ~20-30s per slide, so faster polling adds no information).
     */
    getImportStatus: build.query<ImportStatusResponse, string>({
      query: (elearningId) => `elearnings/${elearningId}/import-status`,
    }),

    getImportCapacity: build.query<ImportCapacity, void>({
      query: () => 'elearnings/import-ppt/capacity',
    }),

    /**
     * Imports a PPTX that has already been uploaded to S3 via the CKFinder connector.
     * Accepts the S3 key returned by uploadPptxToCkfinder() plus the module name.
     * The backend validates that s3Key starts with `content/<tenantId>/` and ends with `.pptx`.
     */
    importPptFromStorage: build.mutation<
      PptImportResponse,
      { s3Key: string; name: string }
    >({
      query: ({ s3Key, name }) => ({
        url: 'elearnings/import-ppt-from-storage',
        method: 'POST',
        body: { s3Key, name },
      }),
      invalidatesTags: [ElearningTag, MyElearningTag],
    }),

    /**
     * Tweaks any slide's HTML using AI. Creates a new frame right after the source.
     * Works on any frame — not PPT-specific.
     */
    tweakSlide: build.mutation<FrameDataType, TweakSlideRequest>({
      query: ({ elearningId, frameId, userInstruction }) => ({
        url: `elearnings/${elearningId}/frames/${frameId}/tweak-slide`,
        method: 'POST',
        body: { userInstruction },
      }),
      invalidatesTags: [FramesTag],
    }),

    /**
     * Regenerates a PPTX-imported slide (regenerate or keep-as-image) as a NEW frame
     * right after the source. Only works on PPTX-imported frames.
     *
     * 422 response shape (out-of-scope user instruction): { message: { refused: true, reason: string } }
     */
    regeneratePptxSlide: build.mutation<FrameDataType, RegenerateSlideRequest>({
      query: ({ elearningId, frameId, mode, userInstruction }) => ({
        url: `elearnings/${elearningId}/frames/${frameId}/regenerate-pptx-slide`,
        method: 'POST',
        body: { mode, ...(userInstruction ? { userInstruction } : {}) },
      }),
      invalidatesTags: [FramesTag],
    }),
  }),
  overrideExisting: false,
});

export const {
  useImportPptMutation,
  useImportPptFromStorageMutation,
  useGetImportStatusQuery,
  useGetImportCapacityQuery,
  useTweakSlideMutation,
  useRegeneratePptxSlideMutation,
} = pptImportApi;

export default pptImportApi;
